import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { usageChartStore } from "@/routes/apt/[apt_code]/units/_components/usage/usage-chart-store"
import { clockStore } from "@/routes/apt/[apt_code]/units/_components/clock/clock-store"
import { weekdayCompareChartStore } from "@/routes/apt/[apt_code]/units/_components/weekday/weekday-compare-chart-store"
import { Clock, Calendar, AlertTriangle, Users, ShieldCheck, Zap } from "lucide-react"
import { RESOURCE_META } from "@/constants/resource-const"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { summaryStore } from "../summary/summary-store"
import { insightBundle } from "./insight-const"
import InsightCard from "./insight-card"
import InsightMessage from "./insight-message"
import Wrapper from "@/components/wrapper"

const InsightView = ({ className = "" }) => {
  const { lang } = langStore()

  const [searchParams] = useSearchParams()

  const resource = searchParams.get("resource") ?? "water"
  const dong = searchParams.get("dong")
  const ho = searchParams.get("ho")

  const { leakSum, leakRate } = usageChartStore()
  const { peak } = clockStore()
  const { series } = weekdayCompareChartStore()
  const { stats } = summaryStore()

  const resourceLabel = useMemo(() => {
    return RESOURCE_META[resource].label[lang]
  }, [resource, lang])

  const insightCards = useMemo(() => {
    const list = []

    if (!dong || !ho) {
      return []
    }

    if (resource === "water") {
      const hasLeakDanger = leakSum > 0 || leakRate > 5

      list.push({
        id: "leak-status",

        type: hasLeakDanger ? "danger" : "success",

        icon: hasLeakDanger ? (
          <AlertTriangle className="w-5 h-5 text-red-500 animate-pulse" />
        ) : (
          <ShieldCheck className="w-5 h-5 text-green-500" />
        ),

        title: hasLeakDanger
          ? insightBundle.leak.detectedTitle[lang]
          : insightBundle.leak.safeTitle[lang],

        desc: hasLeakDanger ? (
          <InsightMessage
            message={insightBundle.leak.detectedDesc[lang]}
            values={{
              rate: leakRate.toFixed(1)
            }}
            highlights={{
              rate: "text-red-600 font-bold"
            }}
          />
        ) : (
          insightBundle.leak.safeDesc[lang]
        )
      })
    }

    if (stats && stats.percent !== undefined) {
      const percent = stats.percent

      const isHigh = percent > 10
      const isLow = percent < -10

      list.push({
        id: "neighborhood-compare",

        type: isHigh ? "warning" : isLow ? "success" : "info",

        icon: (
          <Users
            className={`w-5 h-5 ${
              isHigh ? "text-orange-500" : isLow ? "text-green-500" : "text-blue-500"
            }`}
          />
        ),

        title: insightBundle.compare.title[lang],

        desc: isHigh ? (
          <InsightMessage
            message={insightBundle.compare.highDesc[lang]}
            values={{
              percent: percent.toFixed(1)
            }}
            highlights={{
              percent: "text-orange-600 font-bold"
            }}
          />
        ) : isLow ? (
          <InsightMessage
            message={insightBundle.compare.lowDesc[lang]}
            values={{
              percent: Math.abs(percent).toFixed(1)
            }}
            highlights={{
              percent: "text-green-600 font-bold"
            }}
          />
        ) : (
          <InsightMessage
            message={insightBundle.compare.normalDesc[lang]}
            values={{
              percent: stats.percent || 50
            }}
          />
        )
      })
    }

    const peakHour = peak?.last7days?.hour ?? peak?.today?.hour ?? null

    if (peakHour !== null) {
      const peakText =
        `${String(peakHour).padStart(2, "0")}:00 ~ ` +
        `${String((peakHour + 1) % 24).padStart(2, "0")}:00`

      list.push({
        id: "time-peak",

        type: "info",

        icon: <Clock className="w-5 h-5 text-amber-500" />,

        title: formatMessage(insightBundle.peak.title[lang], {
          resource: resourceLabel
        }),

        desc: (
          <InsightMessage
            message={insightBundle.peak.desc[lang]}
            values={{
              peakText
            }}
            highlights={{
              peakText: "text-amber-600 font-bold"
            }}
          />
        )
      })
    }

    if (series && series.length > 0 && series[0].data && series[0].data.length > 0) {
      const currentData = series[0].data

      const maxVal = Math.max(...currentData)
      const maxIndex = currentData.indexOf(maxVal)

      const peakDay = insightBundle.weekdayPattern.days[maxIndex][lang]

      const weekendAvg = (currentData[0] + currentData[6]) / 2

      const weekdayAvg = currentData.slice(1, 6).reduce((a, b) => a + b, 0) / 5

      const diffPercent =
        weekdayAvg > 0 ? Math.abs(((weekendAvg - weekdayAvg) / weekdayAvg) * 100).toFixed(0) : "0"

      let patternMessage = insightBundle.weekdayPattern.baseDesc[lang]
      let patternValues = { peakDay }

      if (Number(diffPercent) > 15) {
        patternMessage +=
          weekendAvg > weekdayAvg
            ? insightBundle.weekdayPattern.weekendDesc[lang]
            : insightBundle.weekdayPattern.weekdayDesc[lang]

        patternValues = { peakDay, diffPercent }
      }

      list.push({
        id: "weekday-trend",
        type: "info",
        icon: <Calendar className="w-5 h-5 text-emerald-500" />,
        title: insightBundle.weekdayPattern.title[lang],
        desc: <InsightMessage message={patternMessage} values={patternValues} />
      })
    }

    if (list.length === 0) {
      list.push({
        id: "fallback-card",

        type: "info",

        icon: <Zap className="w-5 h-5 text-slate-400" />,

        title: insightBundle.fallback.title[lang],

        desc: insightBundle.fallback.desc[lang]
      })
    }

    return list
  }, [resource, leakSum, leakRate, stats, peak, series, resourceLabel, lang, dong, ho])

  return (
    <Wrapper.shadow className={className}>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          {formatMessage(insightBundle.title[lang], {
            resource: resourceLabel
          })}
        </h3>

        <p className="text-xs text-slate-400 mt-0.5">{insightBundle.subtitle[lang]}</p>
      </div>

      <div className="flex-1 flex flex-col gap-3 overflow-y-visible md:overflow-y-auto md:max-h-90 pr-1 scrollbar-thin">
        {insightCards.map((card) => (
          <InsightCard key={card.id} card={card} />
        ))}
      </div>
    </Wrapper.shadow>
  )
}

export default InsightView
